/**
 * Control characters and special whitespace that can bypass protocol checks.
 * From DOMPurify: https://github.com/cure53/DOMPurify/blob/main/src/regexp.js
 */
const DANGEROUS_WHITESPACE =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;

/**
 * Sanitizes a URL to prevent XSS via dangerous protocols (javascript:, data:, vbscript:, etc.).
 *
 * Only allows http: and https: protocols. Relative URLs, hash fragments, and
 * query strings are considered safe and passed through unchanged.
 *
 * Returns the original URL if safe, empty string if dangerous or invalid.
 *
 * Use this for any URL from an untrusted source (API responses, user input, etc.)
 * before assigning to href attributes or window.location.
 *
 * @example
 * sanitizeUrl('https://www.deviantart.com/user')  // 'https://www.deviantart.com/user'
 * sanitizeUrl('javascript:alert(1)')               // ''
 * sanitizeUrl('/gallery/123')                       // '/gallery/123'
 * sanitizeUrl(undefined)                            // ''
 */
/**
 * Sanitizes a URL and navigates to it via window.location.href.
 * If the URL is dangerous or empty, navigation is skipped.
 *
 * Replaces the common pattern:
 *   const safe = sanitizeUrl(url);
 *   if (safe) { window.location.href = safe; }
 *
 * @example
 * navigateToSanitizedUrl(apiResponse.redirectUrl);
 * navigateToSanitizedUrl('javascript:alert(1)'); // no-op
 */
export function navigateToSanitizedUrl(url: string | undefined | null): void {
  const safe = sanitizeUrl(url);
  if (safe) {
    window.location.href = safe;
  }
}

export function sanitizeUrl(url: string | undefined | null): string {
  if (!url) {
    return '';
  }

  // Strip control characters that can bypass protocol detection
  // e.g., "java\tscript:" or "java\x00script:"
  const normalized = url.replace(DANGEROUS_WHITESPACE, '');

  try {
    // Use a dummy base so relative URLs (inherently safe) resolve to https:
    const parsed = new URL(normalized, 'https://x.invalid');
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return url;
    }
  } catch {
    // URL parsing failed - not a valid URL
  }

  return '';
}
